import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AI_SCOPE_OPTIONS,
  COUNTRY_OPTIONS,
  DEFAULT_DISCOVERY_VALUES,
  deriveUrgencyFromTimeline,
  DISCOVERY_STEPS,
  ENGAGEMENT_TYPES,
  getCountryLabel,
  getCurrencyFromCountry,
  getInvestmentOptions,
  getRegionFromCountry,
  INDUSTRY_GROUPS,
  MAIN_NEEDS,
  ORGANIZATION_TYPES,
  PROJECT_STAGES,
  SOLUTION_TYPES,
  TECHNICAL_SCOPE_OPTIONS,
  TECHNICAL_TEAM_STATUS,
  TIMELINE_OPTIONS,
} from "../data/discoveryWizard";
import {
  getDiscoveryCopy,
  getLocalizedIndustryGroupLabel,
  getLocalizedOptionLabel,
  getLocalizedStepMeta,
  resolveDiscoveryLanguage,
} from "../data/discoveryAssistantI18n";
import {
  authenticateWithProvider,
  createAppointment,
  getAvailabilitySlots,
  isDiscoveryApiConfigured,
  isDiscoveryRelayConfigured,
  requestMagicLink,
  startWizardSession,
  submitWizardLead,
  submitWizardLeadRelay,
  trackSiteVisit,
  updateWizardSession,
  verifyMagicLink,
} from "../lib/discoveryApi";
import { useLanguage } from "../context/LanguageContext";
import {
  AlarmClock,
  BadgeHelp,
  Banknote,
  Bot,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Cloud,
  Clock3,
  Cpu,
  Factory,
  FileText,
  Globe,
  GraduationCap,
  HeartHandshake,
  HeartPulse,
  Hotel,
  Landmark,
  MessageSquare,
  MonitorSmartphone,
  Plane,
  Puzzle,
  Rocket,
  Search,
  Server,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Store,
  Truck,
  TriangleAlert,
  Users,
  Video,
  Wrench,
  Zap,
  Gauge,
} from "lucide-react";

const STORAGE_KEY = "interware_discovery_anonymous_id";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const GOOGLE_SCRIPT_ID = "google-gsi-script";
const APPLE_SCRIPT_ID = "apple-signin-script";
const DISCOVERY_EMAIL_RECIPIENT = "contact-us@interware.dev";
const RELAY_SOURCE = "interware-site-discovery-assistant";
const MAX_SOLUTION_TYPES = 3;

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
const appleRedirectUri = process.env.REACT_APP_APPLE_REDIRECT_URI;

const getSessionInitErrorMessage = (error, language = "en") => {
  const isSpanish = language === "es";
  if (!isDiscoveryApiConfigured()) {
    return isSpanish
      ? "El backend de discovery no está configurado en este deployment. Configurá REACT_APP_API_BASE_URL en las variables de Cloudflare Pages."
      : "Discovery backend is not configured for this deployment. Set REACT_APP_API_BASE_URL in Cloudflare Pages environment variables.";
  }

  const message = typeof error?.message === "string" ? error.message.trim() : "";
  if (!message) {
    return isSpanish
      ? "No se pudo inicializar la sesión de discovery. Intentá nuevamente."
      : "Could not initialize the discovery session. Please try again.";
  }

  if (message.includes("HTTP 404")) {
    return isSpanish
      ? "No se encontraron endpoints de la API de discovery. Revisá REACT_APP_API_BASE_URL y la versión desplegada del backend."
      : "Discovery API endpoints were not found. Check REACT_APP_API_BASE_URL and backend deployment version.";
  }

  if (message.toLowerCase().includes("failed to fetch")) {
    return isSpanish
      ? "No se pudo conectar con el backend de discovery. Revisá disponibilidad del API y configuración CORS."
      : "Could not reach the discovery backend. Check API availability and backend CORS settings.";
  }

  return isSpanish
    ? `No se pudo inicializar la sesión de discovery: ${message}`
    : `Could not initialize the discovery session: ${message}`;
};

const loadExternalScript = (src, scriptId) =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(scriptId);
    if (existing) {
      if (existing.getAttribute("data-loaded") === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.addEventListener(
      "load",
      () => {
        script.setAttribute("data-loaded", "true");
        resolve();
      },
      { once: true },
    );
    script.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), {
      once: true,
    });
    document.head.appendChild(script);
  });

const getOrCreateAnonymousId = () => {
  const existing = localStorage.getItem(STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const id = `anon_${crypto.randomUUID()}`;
  localStorage.setItem(STORAGE_KEY, id);
  return id;
};

const parseUserAgent = () => {
  const ua = navigator.userAgent.toLowerCase();

  const device = /mobile|iphone|android/.test(ua)
    ? "mobile"
    : /ipad|tablet/.test(ua)
      ? "tablet"
      : "desktop";

  const browser =
    ua.includes("edg")
      ? "Edge"
      : ua.includes("chrome")
        ? "Chrome"
        : ua.includes("safari")
          ? "Safari"
          : ua.includes("firefox")
            ? "Firefox"
            : "Other";

  const os =
    ua.includes("windows")
      ? "Windows"
      : ua.includes("mac os")
        ? "macOS"
        : ua.includes("android")
          ? "Android"
          : ua.includes("iphone") || ua.includes("ipad")
            ? "iOS"
            : ua.includes("linux")
              ? "Linux"
              : "Other";

  return { device, browser, os };
};

const normalizeLanguageCode = (value) => {
  if (!value) {
    return "en";
  }
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return "en";
  }
  return normalized.split("-")[0] || "en";
};

const optionButtonClass = (isActive) =>
  `w-full text-left px-4 py-3 rounded-xl border transition-all duration-300 ${
    isActive
      ? "border-cyan-400 bg-cyan-400/10 text-white shadow-[0_0_20px_rgba(34,211,238,0.15)]"
      : "border-zinc-800 bg-zinc-950/60 text-zinc-300 hover:border-zinc-700 hover:bg-zinc-900/80"
  }`;

const industryGroupIcons = {
  financial_and_regulated: Landmark,
  commerce_and_services: ShoppingCart,
  health_education_social_impact: HeartPulse,
  technology_and_media: Cpu,
  industrial_and_enterprise: Factory,
  other: BadgeHelp,
};

const optionIcons = {
  Startup: Rocket,
  "Small business": Briefcase,
  "Medium-sized company": Building2,
  "Large company": Building2,
  Enterprise: Building2,
  "Agency / consulting firm": Users,
  "Government / Public sector": Landmark,
  "Educational institution": GraduationCap,
  "Non-profit organization": HeartHandshake,
  Banking: Landmark,
  Fintech: Banknote,
  Insurance: ShieldCheck,
  Payments: Banknote,
  "Real estate": Building2,
  Retail: Store,
  "E-commerce": ShoppingCart,
  Hospitality: Hotel,
  "Travel / Tourism": Plane,
  "Logistics / Delivery": Truck,
  "Professional services": Briefcase,
  Healthcare: HeartPulse,
  "Wellness / Fitness": HeartPulse,
  Education: GraduationCap,
  "E-learning": GraduationCap,
  "Public services": Landmark,
  "Non-profit / NGO": HeartHandshake,
  "Software / SaaS": MonitorSmartphone,
  "AI / Automation": Bot,
  Telecommunications: Globe,
  Media: Video,
  Entertainment: Video,
  "Marketing / Advertising": MessageSquare,
  Gaming: Zap,
  Manufacturing: Factory,
  Energy: Zap,
  "Agriculture / Agritech": Cloud,
  Construction: Wrench,
  Transportation: Truck,
  "Supply chain": Truck,
  "Enterprise operations": Briefcase,
  "Other industry": BadgeHelp,
  "Not sure / mixed industry": BadgeHelp,
  "Build a new digital product": Rocket,
  "Modernize an existing system": Wrench,
  "Build a mobile app": Smartphone,
  "Build a web platform": MonitorSmartphone,
  "Build backend services or APIs": Server,
  "Integrate existing systems": Puzzle,
  "Add AI / LLM capabilities to an existing product": Bot,
  "Define a technical strategy before development": Search,
  "Not sure yet": BadgeHelp,
  "Mobile application": Smartphone,
  "Web application / SaaS platform": MonitorSmartphone,
  "Backend / API platform": Server,
  "AI assistant / chatbot": Bot,
  "AI-assisted SDLC transformation": Wrench,
  "AI enablement for business operations performance": Gauge,
  "Enterprise system integration": Puzzle,
  "Technical discovery / product strategy": Search,
  "Technical leadership / architecture support": Briefcase,
  "Just an idea": Rocket,
  "We have a business need but no technical definition yet": Search,
  "We have requirements or a brief": Briefcase,
  "We have designs / Figma": Gauge,
  "We have a prototype": Wrench,
  "We have an MVP": Rocket,
  "We have an existing product in production": Building2,
  "The project is blocked or underperforming": TriangleAlert,
  "We need technical assessment before deciding": Search,
  "No, we need full delivery": Building2,
  "Yes, but we need external support": Users,
  "Yes, we need architecture / technical leadership": Briefcase,
  "Yes, we need mobile specialists": Smartphone,
  "Yes, we need backend/cloud specialists": Cloud,
  "Yes, we need AI/LLM expertise": Bot,
  "Yes, but the project is blocked": TriangleAlert,
  "We work with vendors and need independent assessment": Search,
  "We are not sure": BadgeHelp,
  "Mobile apps": Smartphone,
  "Web apps": MonitorSmartphone,
  "Backend services": Server,
  "REST APIs": Server,
  "Cloud infrastructure": Cloud,
  "System integration": Puzzle,
  Databases: Server,
  "Authentication / SSO": ShieldCheck,
  "DevOps / CI-CD": Wrench,
  "Monitoring / observability": Gauge,
  "AI / LLM integrations": Bot,
  "AI assistants / chatbots": Bot,
  "AI workflow automation": Zap,
  "Document processing": FileText,
  "Recommendation engines": Gauge,
  "Content generation": MessageSquare,
  "Retrieval-augmented generation / RAG": Search,
  "Internal knowledge assistants": Bot,
  "Customer-facing AI": Users,
  "Immediately / urgent": AlarmClock,
  "Within the next month": CalendarDays,
  "In the next 2-3 months": CalendarDays,
  "This quarter": CalendarDays,
  "This year": CalendarDays,
  "We are planning for next year": Clock3,
  "We are only exploring options": Search,
  "Fixed-scope project": Briefcase,
  "Discovery phase before development": Search,
  "MVP development": Rocket,
  "Long-term product development": Building2,
  "Time & materials": Clock3,
  "Technical consultancy": Briefcase,
  "Architecture assessment": Search,
  "Staff augmentation": Users,
  "Monthly retainer": Banknote,
};

const formatCopy = (template, replacements) =>
  Object.entries(replacements).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value)),
    template,
  );

const inferPreferredContactMethod = (phone) =>
  String(phone || "").trim().length > 0 ? "WhatsApp" : "Email";

const CONTACT_TREATMENT_OPTIONS = {
  en: ["Mr.", "Ms.", "Mx.", "Dr.", "Prof."],
  es: ["Sr.", "Sra.", "Srta.", "Dr.", "Dra."],
  pt: ["Sr.", "Sra.", "Dr.", "Dra.", "Prof."],
  fr: ["M.", "Mme", "Mlle", "Dr", "Pr"],
  de: ["Herr", "Frau", "Dr.", "Prof.", "Mx."],
  it: ["Sig.", "Sig.ra", "Dott.", "Dott.ssa", "Prof."],
  zh: ["先生", "女士", "博士", "教授", "Mx."],
  ja: ["様", "さん", "先生", "博士", "教授"],
  ko: ["님", "선생님", "박사", "교수", "Mx."],
  ar: ["السيد", "السيدة", "الدكتور", "الدكتورة", "الأستاذ"],
  hi: ["श्री", "श्रीमती", "डॉ.", "प्रो.", "Mx."],
  ru: ["Г-н", "Г-жа", "Д-р", "Проф.", "Mx."],
  tr: ["Sn.", "Dr.", "Prof.", "Bay", "Bayan"],
  nl: ["Dhr.", "Mevr.", "Dr.", "Prof.", "Mx."],
  pl: ["Pan", "Pani", "Dr", "Prof.", "Mx."],
  id: ["Bpk.", "Ibu", "Dr.", "Prof.", "Mx."],
};

const LIST_CONJUNCTION_BY_LANGUAGE = {
  en: "and",
  es: "y",
  pt: "e",
  fr: "et",
  de: "und",
  it: "e",
  zh: "和",
  ja: "と",
  ko: "및",
  ar: "و",
  hi: "और",
  ru: "и",
  tr: "ve",
  nl: "en",
  pl: "i",
  id: "dan",
};

const SUMMARY_TEMPLATE_BY_LANGUAGE = {
  en: "Assessment request from {requester} at {company} for a {solution} project in {industry}, focused on {focus}, currently at the \"{stage}\" stage.",
  es: "Solicitud de evaluación de {requester}, de la empresa {company}, para un proyecto de {solution} en {industry}, enfocado en {focus}, y actualmente en la etapa \"{stage}\".",
  pt: "Solicitação de avaliação de {requester}, da empresa {company}, para um projeto de {solution} em {industry}, focado em {focus}, atualmente na etapa \"{stage}\".",
  fr: "Demande d’évaluation de {requester}, de l’entreprise {company}, pour un projet de {solution} dans le secteur {industry}, axé sur {focus}, actuellement au stade \"{stage}\".",
  de: "Bewertungsanfrage von {requester} aus dem Unternehmen {company} für ein {solution}-Projekt in der Branche {industry}, mit Fokus auf {focus}, aktuell in der Phase \"{stage}\".",
  it: "Richiesta di valutazione da parte di {requester}, dell’azienda {company}, per un progetto di {solution} nel settore {industry}, focalizzato su {focus}, attualmente nella fase \"{stage}\".",
  zh: "{requester}（{company}）提交了项目评估请求：项目类型为{solution}，行业为{industry}，重点在于{focus}，当前阶段为“{stage}”。",
  ja: "{company} の {requester} から、{industry} 分野における {solution} プロジェクトの評価依頼です。主な焦点は {focus}、現在の段階は「{stage}」です。",
  ko: "{company}의 {requester}님이 프로젝트 평가를 요청했습니다. {industry} 분야의 {solution} 프로젝트이며, 핵심 초점은 {focus}, 현재 단계는 \"{stage}\"입니다.",
  ar: "طلب تقييم من {requester} من شركة {company} لمشروع {solution} ضمن قطاع {industry}، يركز على {focus}، وهو حاليًا في مرحلة \"{stage}\".",
  hi: "{company} से {requester} की ओर से मूल्यांकन अनुरोध: {industry} क्षेत्र में {solution} परियोजना, {focus} पर केंद्रित, वर्तमान चरण \"{stage}\"।",
  ru: "Запрос на оценку от {requester} из компании {company}: проект типа {solution} в отрасли {industry}, фокус на {focus}, текущая стадия — \"{stage}\".",
  tr: "{company} şirketinden {requester} tarafından gönderilen değerlendirme talebi: {industry} alanında {solution} projesi, odağı {focus}, mevcut aşama \"{stage}\".",
  nl: "Beoordelingsaanvraag van {requester} van {company} voor een {solution}-project in de sector {industry}, met focus op {focus}, momenteel in fase \"{stage}\".",
  pl: "Prośba o ocenę od {requester} z firmy {company} dla projektu {solution} w branży {industry}, skoncentrowanego na {focus}, obecnie na etapie „{stage}”.",
  id: "Permintaan evaluasi dari {requester} di perusahaan {company} untuk proyek {solution} pada industri {industry}, berfokus pada {focus}, saat ini berada pada tahap \"{stage}\".",
};

const joinLocalizedList = (items, language) => {
  const normalized = items.filter(Boolean);
  if (normalized.length === 0) {
    return "";
  }
  if (normalized.length === 1) {
    return normalized[0];
  }

  const conjunction = LIST_CONJUNCTION_BY_LANGUAGE[language] || LIST_CONJUNCTION_BY_LANGUAGE.en;
  if (normalized.length === 2) {
    return `${normalized[0]} ${conjunction} ${normalized[1]}`;
  }

  return `${normalized.slice(0, -1).join(", ")} ${conjunction} ${normalized[normalized.length - 1]}`;
};

const toLower = (value) => String(value || "").toLowerCase();
const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : value ? [value] : []);
const textMatchesAny = (value, keywords) => {
  const text = toLower(value);
  return keywords.some((keyword) => text.includes(keyword));
};

const filterByAllowedSet = (allOptions, allowedSet) => {
  const filtered = allOptions.filter((option) => allowedSet.has(option));
  return filtered.length > 0 ? filtered : allOptions;
};

const deriveProjectSignals = (formData) => {
  const needs = formData.main_needs || [];
  const solutionTypes = toArray(formData.solution_type);
  const needsText = needs.map((item) => toLower(item)).join(" ");
  const contextText = [
    ...needs.map((item) => toLower(item)),
    ...solutionTypes.map((item) => toLower(item)),
    toLower(formData.project_stage),
    toLower(formData.technical_team_status),
    toLower(formData.business_goal_description),
  ].join(" ");

  const mobile = textMatchesAny(contextText, ["mobile", "ios", "android"]);
  const web = textMatchesAny(contextText, [
    "web",
    "saas",
    "portal",
    "dashboard",
    "e-commerce",
    "booking",
    "ordering",
    "website",
  ]);
  const backend = textMatchesAny(contextText, ["backend", "api", "cloud", "microservice", "serverless"]);
  const ai = textMatchesAny(contextText, [" ai", "ai ", "llm", "chatbot", "automation", "rag"]);
  const integration = textMatchesAny(contextText, ["integration", "integrate", "erp", "crm", "payment", "inventory"]);
  const discovery = textMatchesAny(contextText, [
    "consult",
    "strategy",
    "discovery",
    "assessment",
    "architecture",
    "external architect",
    "technical lead",
    "independent review",
    "not sure",
  ]);
  const maintenance = textMatchesAny(contextText, ["maintenance", "support", "stabilize"]);
  const blocked = textMatchesAny(contextText, ["blocked", "rescue", "underperforming"]);
  const security = textMatchesAny(contextText, ["security", "authentication", "sso", "compliance"]);
  const hasNeeds = needs.length > 0;
  const hasContext = hasNeeds || solutionTypes.length > 0 || Boolean(formData.technical_team_status);

  return {
    hasNeeds,
    hasContext,
    needsText,
    mobile,
    web,
    backend,
    ai,
    integration,
    discovery,
    maintenance,
    blocked,
    security,
  };
};

const getFilteredSolutionTypeOptions = (signals) => {
  if (!signals.hasNeeds) {
    return SOLUTION_TYPES;
  }

  const allowed = new Set([
    "Technical discovery / product strategy",
    "Not sure yet",
  ]);

  if (signals.mobile) {
    allowed.add("Mobile application");
    allowed.add("Backend / API platform");
    allowed.add("Cloud architecture");
  }

  if (signals.web) {
    allowed.add("Web application / SaaS platform");
  }

  if (signals.backend) {
    allowed.add("Backend / API platform");
    allowed.add("Enterprise system integration");
  }

  if (signals.ai) {
    allowed.add("AI assistant / chatbot");
    allowed.add("AI-assisted SDLC transformation");
    allowed.add("AI enablement for business operations performance");
  }

  if (signals.integration) {
    allowed.add("Enterprise system integration");
  }

  return filterByAllowedSet(SOLUTION_TYPES, allowed);
};

const getFilteredTechnicalTeamOptions = (signals) => {
  if (!signals.hasContext) {
    return TECHNICAL_TEAM_STATUS;
  }

  const allowed = new Set([
    "No, we need full delivery",
    "Yes, but we need external support",
    "We are not sure",
  ]);

  if (signals.mobile) {
    allowed.add("Yes, we need mobile specialists");
  }

  if (signals.web || signals.backend || signals.integration) {
    allowed.add("Yes, we need backend/cloud specialists");
  }

  if (signals.ai) {
    allowed.add("Yes, we need AI/LLM expertise");
  }

  if (signals.discovery || signals.blocked) {
    allowed.add("Yes, we need architecture / technical leadership");
    allowed.add("Yes, but the project is blocked");
    allowed.add("We work with vendors and need independent assessment");
  }

  return filterByAllowedSet(TECHNICAL_TEAM_STATUS, allowed);
};

const getFilteredTechnicalScopeOptions = (signals) => {
  if (!signals.hasContext) {
    return TECHNICAL_SCOPE_OPTIONS;
  }

  const allowed = new Set();
  const addCoreArchitecture = () => {
    [
      "Cloud infrastructure",
      "Databases",
      "Authentication / SSO",
      "DevOps / CI-CD",
      "Monitoring / observability",
    ].forEach((item) => allowed.add(item));
  };

  if (signals.mobile || signals.web || signals.backend || signals.ai || signals.maintenance || signals.blocked) {
    addCoreArchitecture();
  }

  if (signals.integration || signals.discovery || signals.blocked) {
    allowed.add("System integration");
  }

  if (signals.security) {
    allowed.add("Authentication / SSO");
  }

  return filterByAllowedSet(TECHNICAL_SCOPE_OPTIONS, allowed);
};

const getFilteredEngagementTypeOptions = (signals) => {
  if (!signals.hasContext) {
    return ENGAGEMENT_TYPES;
  }

  const allowed = new Set(["Not sure yet", "Technical consultancy"]);

  if (signals.discovery) {
    [
      "Discovery phase before development",
      "Architecture assessment",
      "Vendor assessment / independent review",
    ].forEach((item) => allowed.add(item));
  }

  if (signals.mobile || signals.web || signals.backend || signals.ai) {
    [
      "Fixed-scope project",
      "MVP development",
      "Long-term product development",
      "Time & materials",
      "Monthly retainer",
    ].forEach((item) => allowed.add(item));
  }

  if (signals.integration) {
    ["Fixed-scope project", "Time & materials", "Technical consultancy"].forEach((item) =>
      allowed.add(item),
    );
  }

  if (signals.maintenance || signals.blocked) {
    ["Maintenance and support", "Monthly retainer"].forEach((item) => allowed.add(item));
  }

  if (signals.needsText.includes("technical team") || signals.needsText.includes("consultancy")) {
    ["Staff augmentation", "Dedicated team"].forEach((item) => allowed.add(item));
  }

  return filterByAllowedSet(ENGAGEMENT_TYPES, allowed);
};

const ProjectDiscoveryAssistant = () => {
  const { language } = useLanguage();

  const [sessionId, setSessionId] = useState(null);
  const [anonymousId, setAnonymousId] = useState("");
  const [detectedCountry, setDetectedCountry] = useState("");
  const [detectedRegion, setDetectedRegion] = useState("");
  const [detectedCurrency, setDetectedCurrency] = useState("USD");

  const [formData, setFormData] = useState(DEFAULT_DISCOVERY_VALUES);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState("");
  const [initStatus, setInitStatus] = useState("");
  const [isBooting, setIsBooting] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const [authState, setAuthState] = useState({
    provider: "",
    userId: "",
    emailVerified: false,
    magicToken: "",
    authStatus: "",
  });
  const [slots, setSlots] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [scheduleStatus, setScheduleStatus] = useState("");
  const [openIndustryGroupKey, setOpenIndustryGroupKey] = useState(
    INDUSTRY_GROUPS[0]?.key || "",
  );
  const [isGoogleAuthenticating, setIsGoogleAuthenticating] = useState(false);
  const [isAppleAuthenticating, setIsAppleAuthenticating] = useState(false);
  const [turnstileError, setTurnstileError] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(false);

  const interactionRef = useRef({
    startedAt: Date.now(),
    clicks: 0,
    pointerMoves: 0,
    keyPresses: 0,
    scrollDepthPercent: 0,
  });
  const bootedRef = useRef(false);
  const googleButtonContainerRef = useRef(null);
  const googleInitializedRef = useRef(false);
  const googleButtonRenderedRef = useRef(false);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const activeLanguageCode = useMemo(() => normalizeLanguageCode(language), [language]);
  const uiLanguage = useMemo(() => resolveDiscoveryLanguage(activeLanguageCode), [activeLanguageCode]);
  const localizedCopy = useMemo(() => getDiscoveryCopy(uiLanguage), [uiLanguage]);
  const contactTreatmentOptions = useMemo(
    () => CONTACT_TREATMENT_OPTIONS[uiLanguage] || CONTACT_TREATMENT_OPTIONS.en,
    [uiLanguage],
  );
  const localizedSteps = useMemo(
    () => DISCOVERY_STEPS.map((step) => getLocalizedStepMeta(step, uiLanguage)),
    [uiLanguage],
  );
  const currentStep = localizedSteps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / localizedSteps.length) * 100);

  const investmentOptions = useMemo(() => {
    return getInvestmentOptions(formData.currency || detectedCurrency || "USD");
  }, [formData.currency, detectedCurrency]);
  const investmentLabelByValue = useMemo(
    () =>
      Object.fromEntries(
        investmentOptions.map((option) => [option.value, option.label]),
      ),
    [investmentOptions],
  );

  const stepSummary = useMemo(() => {
    const fallbackRequesterByLanguage = {
      es: "la persona solicitante",
      pt: "a pessoa solicitante",
      fr: "la personne de contact",
      de: "die anfragende Person",
      it: "la persona richiedente",
      zh: "请求联系人",
      ja: "ご担当者様",
      ko: "요청자",
      ar: "صاحب الطلب",
      hi: "अनुरोधकर्ता",
      ru: "контактное лицо",
      tr: "talep sahibi",
      nl: "de aanvrager",
      pl: "osoba zgłaszająca",
      id: "kontak pengaju",
    };
    const fallbackCompanyByLanguage = {
      es: "una empresa",
      pt: "uma empresa",
      fr: "une entreprise",
      de: "ein Unternehmen",
      it: "un'azienda",
      zh: "某公司",
      ja: "企業",
      ko: "회사",
      ar: "شركة",
      hi: "एक कंपनी",
      ru: "компания",
      tr: "bir şirket",
      nl: "een bedrijf",
      pl: "firma",
      id: "sebuah perusahaan",
    };
    const fallbackSolutionByLanguage = {
      es: "una solución tecnológica",
      pt: "uma solução tecnológica",
      fr: "une solution technologique",
      de: "eine Technologielösung",
      it: "una soluzione tecnologica",
      zh: "技术方案",
      ja: "技術ソリューション",
      ko: "기술 솔루션",
      ar: "حل تقني",
      hi: "एक टेक्नोलॉजी समाधान",
      ru: "технологическое решение",
      tr: "teknolojik bir çözüm",
      nl: "een technologische oplossing",
      pl: "rozwiązanie technologiczne",
      id: "solusi teknologi",
    };
    const fallbackIndustryByLanguage = {
      es: "una industria no especificada",
      pt: "um setor não especificado",
      fr: "un secteur non spécifié",
      de: "eine nicht spezifizierte Branche",
      it: "un settore non specificato",
      zh: "未指定行业",
      ja: "業界未指定",
      ko: "미지정 산업",
      ar: "قطاع غير محدد",
      hi: "अनिर्दिष्ट उद्योग",
      ru: "неуказанная отрасль",
      tr: "belirtilmemiş sektör",
      nl: "een niet-gespecificeerde sector",
      pl: "nieokreślona branża",
      id: "industri yang belum ditentukan",
    };
    const fallbackFocusByLanguage = {
      es: "el alcance principal del proyecto",
      pt: "o escopo principal do projeto",
      fr: "la portée principale du projet",
      de: "den zentralen Projektumfang",
      it: "l'ambito principale del progetto",
      zh: "项目核心范围",
      ja: "プロジェクトの主要範囲",
      ko: "프로젝트의 핵심 범위",
      ar: "النطاق الرئيسي للمشروع",
      hi: "प्रोजेक्ट का मुख्य दायरा",
      ru: "ключевой фокус проекта",
      tr: "projenin ana kapsamı",
      nl: "de hoofdscope van het project",
      pl: "główny zakres projektu",
      id: "cakupan utama proyek",
    };
    const fallbackStageByLanguage = {
      es: "una etapa aún no especificada",
      pt: "uma etapa ainda não definida",
      fr: "une étape non spécifiée",
      de: "eine noch nicht definierte Phase",
      it: "una fase non ancora definita",
      zh: "未指定阶段",
      ja: "未定義の段階",
      ko: "미정 단계",
      ar: "مرحلة غير محددة",
      hi: "अभी निर्दिष्ट नहीं किया गया चरण",
      ru: "неуказанная стадия",
      tr: "henüz belirtilmemiş aşama",
      nl: "een nog niet gespecificeerde fase",
      pl: "nieokreślony etap",
      id: "tahap yang belum ditentukan",
    };

    const requesterName = [formData.contact_treatment, formData.contact_name]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ");
    const requester =
      requesterName || fallbackRequesterByLanguage[uiLanguage] || "the requesting contact";
    const company = formData.company_name?.trim() || fallbackCompanyByLanguage[uiLanguage] || "a company";
    const localizedSolutionOptions = toArray(formData.solution_type)
      .slice(0, MAX_SOLUTION_TYPES)
      .map((item) => getLocalizedOptionLabel(item, uiLanguage))
      .filter(Boolean);
    const localizedSolution =
      joinLocalizedList(localizedSolutionOptions, uiLanguage) ||
      fallbackSolutionByLanguage[uiLanguage] ||
      "a technology solution";
    const localizedIndustry = formData.industry
      ? getLocalizedOptionLabel(formData.industry, uiLanguage)
      : fallbackIndustryByLanguage[uiLanguage] || "an unspecified industry";
    const localizedNeeds =
      formData.main_needs
        .slice(0, 3)
        .map((item) => getLocalizedOptionLabel(item, uiLanguage))
        .filter(Boolean);
    const localizedFocus =
      joinLocalizedList(localizedNeeds, uiLanguage) ||
      fallbackFocusByLanguage[uiLanguage] ||
      "the project's main scope";
    const localizedStage = formData.project_stage
      ? getLocalizedOptionLabel(formData.project_stage, uiLanguage)
      : fallbackStageByLanguage[uiLanguage] ||
      "an unspecified stage";

    return formatCopy(
      SUMMARY_TEMPLATE_BY_LANGUAGE[uiLanguage] || SUMMARY_TEMPLATE_BY_LANGUAGE.en,
      {
        requester,
        company,
        solution: localizedSolution,
        industry: localizedIndustry,
        focus: localizedFocus,
        stage: localizedStage,
      },
    );
  }, [
    formData.company_name,
    formData.contact_name,
    formData.contact_treatment,
    formData.industry,
    formData.main_needs,
    formData.project_stage,
    formData.solution_type,
    uiLanguage,
  ]);

  const projectSignals = useMemo(() => deriveProjectSignals(formData), [formData]);

  const solutionTypeOptions = useMemo(
    () => getFilteredSolutionTypeOptions(projectSignals),
    [projectSignals],
  );

  const technicalTeamOptions = useMemo(
    () => getFilteredTechnicalTeamOptions(projectSignals),
    [projectSignals],
  );

  const technicalScopeOptions = useMemo(
    () => getFilteredTechnicalScopeOptions(projectSignals),
    [projectSignals],
  );

  const engagementTypeOptions = useMemo(
    () => getFilteredEngagementTypeOptions(projectSignals),
    [projectSignals],
  );

  const loadSlots = useCallback(async () => {
    if (!submitResult?.lead_id) {
      return;
    }

    try {
      const response = await getAvailabilitySlots(formData.timezone);
      setSlots(response.slots || []);
    } catch (error) {
      setScheduleStatus(error.message || "Could not load availability slots.");
    }
  }, [formData.timezone, submitResult?.lead_id]);

  useEffect(() => {
    setFormData((prev) => {
      const existingSolutionTypes = toArray(prev.solution_type);
      if (existingSolutionTypes.length === 0) {
        if (Array.isArray(prev.solution_type)) {
          return prev;
        }
        return { ...prev, solution_type: [] };
      }
      const nextSolutionTypes = existingSolutionTypes.filter((item) => solutionTypeOptions.includes(item));
      if (nextSolutionTypes.length === existingSolutionTypes.length && Array.isArray(prev.solution_type)) {
        return prev;
      }
      return { ...prev, solution_type: nextSolutionTypes };
    });
  }, [solutionTypeOptions]);

  useEffect(() => {
    setFormData((prev) => {
      if (!prev.technical_team_status || technicalTeamOptions.includes(prev.technical_team_status)) {
        return prev;
      }
      return { ...prev, technical_team_status: "" };
    });
  }, [technicalTeamOptions]);

  useEffect(() => {
    setFormData((prev) => {
      if (!prev.engagement_type || engagementTypeOptions.includes(prev.engagement_type)) {
        return prev;
      }
      return { ...prev, engagement_type: "" };
    });
  }, [engagementTypeOptions]);

  useEffect(() => {
    setFormData((prev) => {
      if (!prev.technical_scope.length) {
        return prev;
      }
      const nextScope = prev.technical_scope.filter((item) => technicalScopeOptions.includes(item));
      if (nextScope.length === prev.technical_scope.length) {
        return prev;
      }
      return { ...prev, technical_scope: nextScope };
    });
  }, [technicalScopeOptions]);

  useEffect(() => {
    setFormData((prev) => {
      if (prev.preferred_language === activeLanguageCode) {
        return prev;
      }
      return { ...prev, preferred_language: activeLanguageCode };
    });
  }, [activeLanguageCode]);

  useEffect(() => {
    const clicksListener = () => {
      interactionRef.current.clicks += 1;
    };
    const keyListener = () => {
      interactionRef.current.keyPresses += 1;
    };

    let canCountPointer = true;
    const pointerListener = () => {
      if (!canCountPointer) {
        return;
      }
      canCountPointer = false;
      interactionRef.current.pointerMoves += 1;
      setTimeout(() => {
        canCountPointer = true;
      }, 180);
    };

    const scrollListener = () => {
      const totalScrollable =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (totalScrollable <= 0) {
        return;
      }
      const percent = Math.round((window.scrollY / totalScrollable) * 100);
      if (percent > interactionRef.current.scrollDepthPercent) {
        interactionRef.current.scrollDepthPercent = Math.min(100, percent);
      }
    };

    window.addEventListener("click", clicksListener);
    window.addEventListener("keydown", keyListener);
    window.addEventListener("pointermove", pointerListener);
    window.addEventListener("scroll", scrollListener);

    return () => {
      window.removeEventListener("click", clicksListener);
      window.removeEventListener("keydown", keyListener);
      window.removeEventListener("pointermove", pointerListener);
      window.removeEventListener("scroll", scrollListener);
    };
  }, []);

  useEffect(() => {
    if (bootedRef.current) {
      return;
    }
    bootedRef.current = true;

    const boot = async () => {
      try {
        const anonId = getOrCreateAnonymousId();
        setAnonymousId(anonId);

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        const browserLanguage = navigator.language || "en-US";
        const { device, browser, os } = parseUserAgent();

        const params = new URLSearchParams(window.location.search);
        const utmSource = params.get("utm_source") || undefined;
        const utmCampaign = params.get("utm_campaign") || undefined;
        const utmMedium = params.get("utm_medium") || undefined;
        const utmTerm = params.get("utm_term") || undefined;
        const utmContent = params.get("utm_content") || undefined;

        setFormData((prev) => ({
          ...prev,
          timezone,
          preferred_language: activeLanguageCode,
        }));

        if (!isDiscoveryApiConfigured()) {
          setInitStatus(
            uiLanguage === "es"
              ? isDiscoveryRelayConfigured()
                ? "El backend de discovery no está configurado aún, pero el envío funcionará por relay externo."
                : "El backend de discovery no está configurado en este entorno. Configurá REACT_APP_API_BASE_URL y redeployá este preview."
              : isDiscoveryRelayConfigured()
                ? "Discovery backend is not configured yet, but submission will use the external relay."
                : "Discovery backend is not configured in this environment. Configure REACT_APP_API_BASE_URL and redeploy this preview.",
          );
          return;
        }

        await trackSiteVisit({
          path: window.location.pathname,
          anonymous_id: anonId,
          browser,
          os,
          device,
          referrer: document.referrer || undefined,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_medium: utmMedium,
        }).catch(() => {});

        const session = await startWizardSession({
          anonymous_id: anonId,
          current_path: window.location.pathname,
          browser_language: browserLanguage,
          timezone,
          detected_timezone: timezone,
          referrer: document.referrer || undefined,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_medium: utmMedium,
          utm_term: utmTerm,
          utm_content: utmContent,
          browser,
          os,
          device,
        });

        setSessionId(session.session_id);
        setDetectedCountry(session.detected_country || "");
        setDetectedRegion(session.detected_region || "Global / Multi-region");
        setDetectedCurrency(session.detected_currency || "USD");

        setFormData((prev) => ({
          ...prev,
          currency: session.detected_currency || prev.currency,
          declared_region: prev.declared_region || session.detected_region || "",
        }));
      } catch (error) {
        console.error(error);
        setInitStatus(getSessionInitErrorMessage(error, uiLanguage));
      } finally {
        setIsBooting(false);
      }
    };

    boot();
  }, [activeLanguageCode, uiLanguage]);

  useEffect(() => {
    const tokenFromUrl = new URLSearchParams(window.location.search).get("token");
    if (!tokenFromUrl) {
      return;
    }

    const verifyFromUrl = async () => {
      try {
        const result = await verifyMagicLink(tokenFromUrl);
        setAuthState((prev) => ({
          ...prev,
          provider: "magic_link",
          userId: result.authenticated_user_id,
          emailVerified: result.email_verified,
          authStatus: "Magic link verified successfully.",
        }));
        setScheduleStatus("Magic link verified. You can now schedule your call.");
      } catch (error) {
        setScheduleStatus(error.message || "Could not verify magic link token.");
      } finally {
        const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };

    verifyFromUrl();
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) {
      return;
    }

    let unmounted = false;

    const setupTurnstile = async () => {
      try {
        await loadExternalScript(
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
          TURNSTILE_SCRIPT_ID,
        );

        if (unmounted || !window.turnstile || turnstileWidgetIdRef.current !== null) {
          return;
        }

        const widgetId = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: turnstileSiteKey,
          theme: "auto",
          callback: (token) => {
            setTurnstileToken(token);
            setTurnstileError("");
          },
          "error-callback": () => {
            setTurnstileToken("");
            setTurnstileError(
              uiLanguage === "es"
                ? "Falló la verificación de Turnstile. Intentá nuevamente."
                : "Turnstile verification failed. Please retry.",
            );
          },
          "expired-callback": () => {
            setTurnstileToken("");
            setTurnstileError(
              uiLanguage === "es"
                ? "El token de Turnstile expiró. Verificá nuevamente."
                : "Turnstile token expired. Please verify again.",
            );
          },
        });

        turnstileWidgetIdRef.current = widgetId;
        setTurnstileReady(true);
      } catch (error) {
        setTurnstileError(
          error.message ||
            (uiLanguage === "es"
              ? "No se pudo cargar el widget de Turnstile."
              : "Could not load Turnstile widget."),
        );
      }
    };

    setupTurnstile();

    return () => {
      unmounted = true;
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (_error) {
          // Ignore cleanup errors.
        }
      }
      turnstileWidgetIdRef.current = null;
      setTurnstileReady(false);
    };
  }, [stepIndex, uiLanguage]);

  useEffect(() => {
    if (!googleClientId || !googleButtonContainerRef.current || !submitResult) {
      return;
    }

    let unmounted = false;
    const setupGoogleButton = async () => {
      try {
        await loadExternalScript("https://accounts.google.com/gsi/client", GOOGLE_SCRIPT_ID);
        if (unmounted || !window.google?.accounts?.id) {
          return;
        }

        if (!googleInitializedRef.current) {
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: async (response) => {
              if (!response?.credential) {
                setScheduleStatus("Google sign-in did not return a credential.");
                setIsGoogleAuthenticating(false);
                return;
              }

              try {
                const result = await authenticateWithProvider("google", {
                  idToken: response.credential,
                  email: formData.email,
                });

                setAuthState({
                  provider: "google",
                  userId: result.authenticated_user_id,
                  emailVerified: result.email_verified,
                  magicToken: "",
                  authStatus: "Google authentication completed.",
                });

                setScheduleStatus("");
                await loadSlots();
              } catch (error) {
                setScheduleStatus(error.message || "Google authentication failed.");
              } finally {
                setIsGoogleAuthenticating(false);
              }
            },
          });
          googleInitializedRef.current = true;
        }

        if (!googleButtonRenderedRef.current) {
          googleButtonContainerRef.current.innerHTML = "";
          window.google.accounts.id.renderButton(googleButtonContainerRef.current, {
            theme: "filled_black",
            size: "large",
            shape: "pill",
            text: "continue_with",
            width: 260,
          });
          googleButtonRenderedRef.current = true;
        }
      } catch (error) {
        setScheduleStatus(error.message || "Could not load Google sign-in.");
      }
    };

    setupGoogleButton();

    return () => {
      unmounted = true;
    };
  }, [submitResult, formData.email, loadSlots]);

  const pushSessionUpdate = async (nextStepIndex) => {
    if (!sessionId) {
      return;
    }

    try {
      const interactionSignals = {
        clicks: interactionRef.current.clicks,
        pointer_moves: interactionRef.current.pointerMoves,
        key_presses: interactionRef.current.keyPresses,
        scroll_depth_percent: interactionRef.current.scrollDepthPercent,
        completion_seconds: Math.round((Date.now() - interactionRef.current.startedAt) / 1000),
      };

      await updateWizardSession(sessionId, {
        current_step: nextStepIndex + 1,
        completion_rate: (nextStepIndex + 1) / localizedSteps.length,
        step_key: currentStep.key,
        step_value: formData[currentStep.key],
        interaction_signals: interactionSignals,
      });
    } catch (_error) {
      // Non-blocking for UX.
    }
  };

  const toggleArrayValue = (key, value) => {
    setFormData((prev) => {
      const existing = prev[key] || [];
      const hasValue = existing.includes(value);
      return {
        ...prev,
        [key]: hasValue ? existing.filter((item) => item !== value) : [...existing, value],
      };
    });
  };

  const toggleSolutionTypeValue = (value) => {
    setFormData((prev) => {
      const existing = toArray(prev.solution_type);
      const hasValue = existing.includes(value);

      if (hasValue) {
        setErrors("");
        return { ...prev, solution_type: existing.filter((item) => item !== value) };
      }

      if (value === "Not sure yet") {
        setErrors("");
        return { ...prev, solution_type: [value] };
      }

      const cleaned = existing.filter((item) => item !== "Not sure yet");
      if (cleaned.length >= MAX_SOLUTION_TYPES) {
        setErrors(
          uiLanguage === "es"
            ? `Podés elegir hasta ${MAX_SOLUTION_TYPES} tipos de solución.`
            : `You can choose up to ${MAX_SOLUTION_TYPES} solution types.`,
        );
        return prev;
      }

      setErrors("");
      return { ...prev, solution_type: [...cleaned, value] };
    });
  };

  const validateStep = () => {
    switch (currentStep.key) {
      case "organization_type":
        return Boolean(formData.organization_type);
      case "industry":
        return Boolean(formData.industry && formData.industry_group);
      case "region":
        if (formData.is_multi_region_project) {
          return true;
        }
        return Boolean(formData.declared_country);
      case "main_needs":
        return formData.main_needs.length > 0;
      case "solution_type":
        return toArray(formData.solution_type).length > 0;
      case "project_stage":
        return Boolean(formData.project_stage);
      case "technical_team_status":
        return Boolean(formData.technical_team_status);
      case "technical_scope":
        return formData.technical_scope.length > 0;
      case "timeline":
        return Boolean(formData.timeline);
      case "investment_range":
        return Boolean(formData.investment_range);
      case "engagement_type":
        return Boolean(formData.engagement_type);
      case "business_goal_description":
        return formData.business_goal_description.trim().length >= 15;
      case "contact":
        return (
          formData.contact_name.trim().length >= 2 &&
          formData.company_name.trim().length >= 2 &&
          /.+@.+\..+/.test(formData.email)
        );
      default:
        return true;
    }
  };

  const nextStep = async () => {
    setErrors("");
    if (!validateStep()) {
      setErrors(localizedCopy.stepRequiredError);
      return;
    }

    const nextIndex = Math.min(stepIndex + 1, localizedSteps.length - 1);
    await pushSessionUpdate(nextIndex);
    setStepIndex(nextIndex);
  };

  const prevStep = async () => {
    setErrors("");
    const prevIndex = Math.max(stepIndex - 1, 0);
    await pushSessionUpdate(prevIndex);
    setStepIndex(prevIndex);
  };

  const handleCountryChange = (countryCode) => {
    const region = getRegionFromCountry(countryCode);
    const currency = getCurrencyFromCountry(countryCode);
    setFormData((prev) => ({
      ...prev,
      declared_country: countryCode,
      declared_region: region,
      currency,
    }));
  };

  const handleSubmit = async () => {
    setErrors("");
    if (turnstileSiteKey && !turnstileToken) {
      setErrors(
        uiLanguage === "es"
          ? "Completá la verificación de Turnstile antes de enviar."
          : "Please complete Turnstile verification before submitting.",
      );
      return;
    }
    setIsSubmitting(true);

    try {
      const safeAnonymousId = anonymousId || getOrCreateAnonymousId();
      if (!anonymousId) {
        setAnonymousId(safeAnonymousId);
      }

      const derivedPlatforms = [
        projectSignals.mobile ? "Mobile apps" : null,
        projectSignals.web ? "Web apps" : null,
        projectSignals.backend ? "Backend services" : null,
      ].filter(Boolean);
      const derivedEnterpriseSystems = Array.from(
        new Set([
          ...(formData.technical_scope.includes("System integration")
            ? ["System integration"]
            : []),
          ...(projectSignals.integration ? ["System integration"] : []),
        ]),
      );
      const preferredContactMethod = inferPreferredContactMethod(formData.phone);
      const selectedSolutionTypes = toArray(formData.solution_type).slice(0, MAX_SOLUTION_TYPES);
      const primarySolutionType = selectedSolutionTypes[0] || "";
      const solutionText = selectedSolutionTypes.join(" ").toLowerCase();

      const solutionCategory = solutionText.includes("ai")
        ? "ai_automation"
        : solutionText.includes("mobile")
          ? "mobile"
          : solutionText.includes("web")
            ? "web_platform"
            : solutionText.includes("backend")
              ? "backend_api"
              : "consulting";

      const interactionSignals = {
        clicks: interactionRef.current.clicks,
        pointer_moves: interactionRef.current.pointerMoves,
        key_presses: interactionRef.current.keyPresses,
        scroll_depth_percent: interactionRef.current.scrollDepthPercent,
        completion_seconds: Math.round((Date.now() - interactionRef.current.startedAt) / 1000),
      };

      const payload = {
        ...formData,
        solution_type: primarySolutionType,
        solution_types: selectedSolutionTypes,
        preferred_contact_method: preferredContactMethod,
        assessment_summary: stepSummary,
        session_id: sessionId || `session_pending_${safeAnonymousId}`,
        anonymous_id: safeAnonymousId,
        detected_country: detectedCountry || undefined,
        detected_region: detectedRegion || undefined,
        detected_currency: detectedCurrency || undefined,
        detected_timezone: formData.timezone || undefined,
        browser_language: navigator.language,
        timezone: formData.timezone,
        platforms: derivedPlatforms,
        enterprise_systems: derivedEnterpriseSystems,
        solution_category: solutionCategory,
        urgency_level: deriveUrgencyFromTimeline(formData.timeline),
        interaction_signals: interactionSignals,
        honeypot,
        turnstile_token: turnstileToken || undefined,
      };

      const relayPayload = {
        source: RELAY_SOURCE,
        recipient: DISCOVERY_EMAIL_RECIPIENT,
        submitted_at: new Date().toISOString(),
        browser_language: navigator.language,
        timezone: formData.timezone,
        ui_language: uiLanguage,
        website_language: activeLanguageCode,
        turnstile_token: turnstileToken || undefined,
        lead_payload: payload,
      };

      const shouldTryBackend = Boolean(sessionId && isDiscoveryApiConfigured());
      const shouldTryRelay = isDiscoveryRelayConfigured();

      if (!shouldTryBackend && !shouldTryRelay) {
        setErrors(
          uiLanguage === "es"
            ? "No hay backend ni relay externo configurados para enviar esta solicitud. Activá al menos uno de los dos."
            : "There is no backend or external relay configured to submit this request. Enable at least one.",
        );
        return;
      }

      const [backendAttempt, relayAttempt] = await Promise.all([
        shouldTryBackend
          ? submitWizardLead(payload)
              .then((result) => ({ ok: true, result }))
              .catch((error) => ({ ok: false, error }))
          : Promise.resolve(null),
        shouldTryRelay
          ? submitWizardLeadRelay(relayPayload)
              .then((result) => ({ ok: true, result }))
              .catch((error) => ({ ok: false, error }))
          : Promise.resolve(null),
      ]);

      if (backendAttempt?.ok) {
        setSubmitResult({ ...backendAttempt.result, source: "backend" });
        await pushSessionUpdate(localizedSteps.length - 1);
        return;
      }

      if (relayAttempt?.ok) {
        setSubmitResult({
          success: true,
          source: "relay",
          relay_delivery_id: relayAttempt.result?.lead_id || relayAttempt.result?.id || `relay_${Date.now()}`,
          lead_score: relayAttempt.result?.lead_score ?? "-",
          lead_priority: relayAttempt.result?.lead_priority ?? "pending",
          suggested_offering: relayAttempt.result?.suggested_offering ?? "Pending backend classification",
        });
        setErrors("");
        return;
      }

      const backendMessage = backendAttempt?.error ? String(backendAttempt.error?.message || backendAttempt.error) : "";
      const relayMessage = relayAttempt?.error ? String(relayAttempt.error?.message || relayAttempt.error) : "";
      const combinedFailure = [backendMessage, relayMessage].filter(Boolean).join(" | ");

      setErrors(
        combinedFailure ||
          (uiLanguage === "es"
            ? "No se pudo enviar por backend ni por relay externo."
            : "Could not submit via backend or external relay."),
      );
    } catch (error) {
      const message = String(error?.message || "");
      setErrors(
        message ||
          (uiLanguage === "es"
            ? "No se pudo enviar la evaluación de tu proyecto."
            : "Could not submit your project assessment."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerGoogleSignIn = async () => {
    setScheduleStatus("");
    if (!googleClientId) {
      setScheduleStatus("Google sign-in is not configured in frontend environment.");
      return;
    }

    try {
      setIsGoogleAuthenticating(true);
      await loadExternalScript("https://accounts.google.com/gsi/client", GOOGLE_SCRIPT_ID);

      if (!window.google?.accounts?.id) {
        throw new Error("Google Identity SDK is not available.");
      }

      window.google.accounts.id.prompt((notification) => {
        if (
          notification?.isNotDisplayed?.() ||
          notification?.isSkippedMoment?.()
        ) {
          setScheduleStatus(
            "Google One Tap was not displayed. Please use the official Google button below.",
          );
          setIsGoogleAuthenticating(false);
        }
      });
    } catch (error) {
      setScheduleStatus(error.message || "Could not start Google sign-in.");
      setIsGoogleAuthenticating(false);
    }
  };

  const triggerAppleSignIn = async () => {
    setScheduleStatus("");
    if (!appleClientId || !appleRedirectUri) {
      setScheduleStatus(
        "Apple sign-in is not configured. Set REACT_APP_APPLE_CLIENT_ID and REACT_APP_APPLE_REDIRECT_URI.",
      );
      return;
    }

    try {
      setIsAppleAuthenticating(true);
      await loadExternalScript(
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
        APPLE_SCRIPT_ID,
      );

      if (!window.AppleID?.auth) {
        throw new Error("Apple Sign In SDK is not available.");
      }

      const nonce = `nonce_${crypto.randomUUID().replaceAll("-", "")}`;
      const state = `state_${crypto.randomUUID().replaceAll("-", "")}`;
      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: "name email",
        redirectURI: appleRedirectUri,
        state,
        nonce,
        usePopup: true,
      });

      const appleResponse = await window.AppleID.auth.signIn();
      const idToken = appleResponse?.authorization?.id_token;
      if (!idToken) {
        throw new Error("Apple sign-in did not return an identity token.");
      }

      const result = await authenticateWithProvider("apple", {
        idToken,
        email: formData.email,
      });

      setAuthState({
        provider: "apple",
        userId: result.authenticated_user_id,
        emailVerified: result.email_verified,
        magicToken: "",
        authStatus: "Apple authentication completed.",
      });
      await loadSlots();
    } catch (error) {
      setScheduleStatus(error.message || "Apple authentication failed.");
    } finally {
      setIsAppleAuthenticating(false);
    }
  };

  const handleAuthProvider = async (provider) => {
    setScheduleStatus("");

    try {
      if (provider === "google") {
        await triggerGoogleSignIn();
        return;
      }

      if (provider === "apple") {
        await triggerAppleSignIn();
        return;
      }

      if (provider === "magic_link") {
        await requestMagicLink(formData.email);
        setAuthState((prev) => ({
          ...prev,
          provider,
          authStatus: "Magic link sent. Check your email and open the verification link.",
        }));
        return;
      }
    } catch (error) {
      setScheduleStatus(error.message || "Authentication failed.");
    }
  };

  const handleVerifyMagicLink = async () => {
    if (!authState.magicToken.trim()) {
      setScheduleStatus("Paste the magic link token to continue.");
      return;
    }

    try {
      const result = await verifyMagicLink(authState.magicToken.trim());
      setAuthState((prev) => ({
        ...prev,
        provider: "magic_link",
        userId: result.authenticated_user_id,
        emailVerified: result.email_verified,
        authStatus: "Magic link verified successfully.",
      }));
      await loadSlots();
    } catch (error) {
      setScheduleStatus(error.message || "Magic link verification failed.");
    }
  };

  const scheduleAppointment = async () => {
    if (!selectedSlotId) {
      setScheduleStatus("Choose an available slot first.");
      return;
    }

    if (!authState.userId || !authState.emailVerified) {
      setScheduleStatus("Please authenticate before scheduling.");
      return;
    }

    try {
      const result = await createAppointment({
        lead_id: submitResult.lead_id,
        slot_id: selectedSlotId,
        auth_provider: authState.provider,
        authenticated_user_id: authState.userId,
        email_verified: authState.emailVerified,
        timezone: formData.timezone || "UTC",
        notes: "Scheduled from website wizard",
      });

      setScheduleStatus(`Appointment confirmed for ${result.appointment.start_time}.`);
      await loadSlots();
    } catch (error) {
      setScheduleStatus(error.message || "Could not schedule appointment.");
    }
  };

  const getIconForOption = (option) => optionIcons[option] || Briefcase;

  const renderOptionButton = ({ value, selected, onClick }) => {
    const Icon = getIconForOption(value);
    return (
      <button
        key={value}
        type="button"
        className={optionButtonClass(selected)}
        onClick={onClick}
      >
        <span className="flex items-start gap-3">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
          <span>{getLocalizedOptionLabel(value, uiLanguage)}</span>
        </span>
      </button>
    );
  };

  const renderSingleOptions = (items, key) => (
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((option) =>
        renderOptionButton({
          value: option,
          selected: formData[key] === option,
          onClick: () => setFormData((prev) => ({ ...prev, [key]: option })),
        }),
      )}
    </div>
  );

  const renderStep = () => {
    switch (currentStep.type) {
      case "single":
        if (currentStep.key === "organization_type") {
          return renderSingleOptions(ORGANIZATION_TYPES, "organization_type");
        }
        if (currentStep.key === "project_stage") {
          return renderSingleOptions(PROJECT_STAGES, "project_stage");
        }
        if (currentStep.key === "technical_team_status") {
          return renderSingleOptions(technicalTeamOptions, "technical_team_status");
        }
        if (currentStep.key === "timeline") {
          return renderSingleOptions(TIMELINE_OPTIONS, "timeline");
        }
        if (currentStep.key === "engagement_type") {
          return renderSingleOptions(engagementTypeOptions, "engagement_type");
        }
        return null;

      case "industry":
        return (
          <div className="space-y-3">
            {INDUSTRY_GROUPS.map((group) => (
              <div key={group.key} className="rounded-2xl border border-zinc-800 bg-zinc-950/50">
                <button
                  type="button"
                  className="w-full px-4 py-3 text-left flex items-center justify-between gap-3"
                  onClick={() =>
                    setOpenIndustryGroupKey((prev) => (prev === group.key ? "" : group.key))
                  }
                >
                  <span className="flex items-center gap-3">
                    {React.createElement(industryGroupIcons[group.key] || Briefcase, {
                      className: "h-4 w-4 text-cyan-300",
                    })}
                    <span className="text-sm uppercase tracking-widest text-cyan-400/90">
                      {getLocalizedIndustryGroupLabel(group.label, uiLanguage)}
                    </span>
                  </span>
                  {openIndustryGroupKey === group.key ? (
                    <ChevronDown className="h-4 w-4 text-zinc-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-zinc-500" />
                  )}
                </button>

                <div
                  className={`grid gap-3 sm:grid-cols-2 px-4 pb-4 transition-all duration-300 ${
                    openIndustryGroupKey === group.key
                      ? "max-h-[800px] opacity-100"
                      : "max-h-0 opacity-0 overflow-hidden p-0"
                  }`}
                >
                  {group.options.map((option) =>
                    renderOptionButton({
                      value: option,
                      selected: formData.industry === option,
                      onClick: () =>
                        setFormData((prev) => ({
                          ...prev,
                          industry: option,
                          industry_group: group.label,
                        })),
                    }),
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case "region":
        return (
          <div className="space-y-6">
            <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/60 text-zinc-300 text-sm">
              {localizedCopy.detectedCountryPrefix}{" "}
              <span className="text-white font-medium">
                {detectedCountry
                  ? getCountryLabel(detectedCountry)
                  : localizedCopy.unknownLocation}
              </span>
              .
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <button
                type="button"
                className={optionButtonClass(
                  Boolean(detectedCountry) && formData.declared_country === detectedCountry && !formData.is_multi_region_project,
                )}
                onClick={() => {
                  if (detectedCountry) {
                    handleCountryChange(detectedCountry);
                    setFormData((prev) => ({ ...prev, is_multi_region_project: false }));
                  }
                }}
                disabled={!detectedCountry}
              >
                <span className="flex items-start gap-3">
                  <Globe className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                  <span>
                    {localizedCopy.useDetectedCountry}{" "}
                    {detectedCountry ? getCountryLabel(detectedCountry) : localizedCopy.unknownLocation}
                  </span>
                </span>
              </button>

              <button
                type="button"
                className={optionButtonClass(!formData.is_multi_region_project)}
                onClick={() => setFormData((prev) => ({ ...prev, is_multi_region_project: false }))}
              >
                <span className="flex items-start gap-3">
                  <Search className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                  <span>{localizedCopy.chooseAnotherCountry}</span>
                </span>
              </button>

              <button
                type="button"
                className={optionButtonClass(formData.is_multi_region_project)}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    is_multi_region_project: true,
                    declared_country: "Global",
                    declared_region: "Global / Multi-region",
                    currency: "USD",
                  }))
                }
              >
                <span className="flex items-start gap-3">
                  <Globe className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                  <span>{localizedCopy.internationalProject}</span>
                </span>
              </button>
            </div>

            {!formData.is_multi_region_project && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">
                    {localizedCopy.projectCountryLabel}
                  </label>
                  <select
                    className="mt-2 w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
                    value={formData.declared_country}
                    onChange={(event) => handleCountryChange(event.target.value)}
                  >
                    <option value="">{localizedCopy.selectCountry}</option>
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">
                    {localizedCopy.timezoneLabel}
                  </label>
                  <input
                    className="mt-2 w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
                    value={formData.timezone}
                    onChange={(event) => setFormData((prev) => ({ ...prev, timezone: event.target.value }))}
                    placeholder={localizedCopy.timezonePlaceholder}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case "multi":
        if (currentStep.key === "solution_type") {
          const selectedCount = toArray(formData.solution_type).length;
          return (
            <div className="space-y-4">
              <p className="text-xs text-zinc-500">
                {uiLanguage === "es"
                  ? `Podés elegir hasta ${MAX_SOLUTION_TYPES} opciones.`
                  : `You can choose up to ${MAX_SOLUTION_TYPES} options.`}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {solutionTypeOptions.map((option) =>
                  renderOptionButton({
                    value: option,
                    selected: toArray(formData.solution_type).includes(option),
                    onClick: () => toggleSolutionTypeValue(option),
                  }),
                )}
              </div>
              <p className="text-xs text-zinc-500">
                {uiLanguage === "es"
                  ? `${selectedCount}/${MAX_SOLUTION_TYPES} seleccionadas`
                  : `${selectedCount}/${MAX_SOLUTION_TYPES} selected`}
              </p>
            </div>
          );
        }

        return (
          <div className="grid gap-3 sm:grid-cols-2">
            {MAIN_NEEDS.map((need) =>
              renderOptionButton({
                value: need,
                selected: formData.main_needs.includes(need),
                onClick: () => toggleArrayValue("main_needs", need),
              }),
            )}
          </div>
        );

      case "scope": {
        const selectedSolutionTypes = toArray(formData.solution_type);
        const showAiScope =
          formData.main_needs.some((item) => item.toLowerCase().includes("ai") || item.toLowerCase().includes("llm")) ||
          selectedSolutionTypes.some((item) => {
            const normalized = String(item || "").toLowerCase();
            return normalized.includes("ai") || normalized.includes("llm");
          });

        return (
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-cyan-400/90 mb-3">
                {localizedCopy.technicalScopeLabel}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {technicalScopeOptions.map((scope) =>
                  renderOptionButton({
                    value: scope,
                    selected: formData.technical_scope.includes(scope),
                    onClick: () => toggleArrayValue("technical_scope", scope),
                  }),
                )}
              </div>
            </div>

            {showAiScope && (
              <div>
                <p className="text-xs uppercase tracking-widest text-cyan-400/90 mb-3">
                  {localizedCopy.aiDetailLabel}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {AI_SCOPE_OPTIONS.map((scope) =>
                    renderOptionButton({
                      value: scope,
                      selected: formData.ai_scope.includes(scope),
                      onClick: () => toggleArrayValue("ai_scope", scope),
                    }),
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }

      case "investment":
        return (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {investmentOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={optionButtonClass(formData.investment_range === option.value)}
                  onClick={() => setFormData((prev) => ({ ...prev, investment_range: option.value }))}
                >
                  <span className="flex items-start gap-3">
                    <Banknote className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                    <span>{option.label}</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              {localizedCopy.investmentCurrency}: {formData.currency || "USD"}
            </p>
          </div>
        );

      case "textarea":
        return (
          <textarea
            rows={6}
            value={formData.business_goal_description}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                business_goal_description: event.target.value,
              }))
            }
            className="w-full rounded-2xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-4 focus:outline-none focus:border-cyan-400"
            placeholder={localizedCopy.businessGoalPlaceholder}
          />
        );

      case "contact":
        return (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[170px_1fr]">
              <select
                className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
                value={formData.contact_treatment}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, contact_treatment: event.target.value }))
                }
              >
                <option value="">{localizedCopy.contactTreatmentPlaceholder}</option>
                {contactTreatmentOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <input
                className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
                placeholder={localizedCopy.contactNamePlaceholder}
                value={formData.contact_name}
                onChange={(event) => setFormData((prev) => ({ ...prev, contact_name: event.target.value }))}
              />
            </div>
            <input
              className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
              placeholder={localizedCopy.companyPlaceholder}
              value={formData.company_name}
              onChange={(event) => setFormData((prev) => ({ ...prev, company_name: event.target.value }))}
            />
            <input
              className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
              placeholder={localizedCopy.contactRolePlaceholder}
              value={formData.contact_role}
              onChange={(event) => setFormData((prev) => ({ ...prev, contact_role: event.target.value }))}
            />
            <input
              type="email"
              className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
              placeholder={localizedCopy.workEmailPlaceholder}
              value={formData.email}
              onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
            />
            <input
              className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
              placeholder={localizedCopy.phonePlaceholder}
              value={formData.phone}
              onChange={(event) => {
                const nextPhone = event.target.value;
                setFormData((prev) => ({
                  ...prev,
                  phone: nextPhone,
                  preferred_contact_method: inferPreferredContactMethod(nextPhone),
                }));
              }}
            />
            <p className="text-xs text-zinc-500">{localizedCopy.contactMethodHint}</p>

            <input
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
              placeholder="Leave this field empty"
            />

            {turnstileSiteKey ? (
              <div className="space-y-2">
                <div
                  ref={turnstileContainerRef}
                  className="min-h-[65px] rounded-xl border border-zinc-800 bg-zinc-950/60 p-2"
                />
                {!turnstileReady && (
                  <p className="text-xs text-zinc-500">{localizedCopy.loadingAntiSpam}</p>
                )}
                {turnstileError && <p className="text-xs text-red-300">{turnstileError}</p>}
              </div>
            ) : null}
          </div>
        );

      case "summary":
        return (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/60">
                <p className="text-zinc-500 uppercase text-[11px] tracking-wider">{localizedCopy.summaryOrganization}</p>
                <p className="text-zinc-200 mt-1">
                  {formData.organization_type
                    ? getLocalizedOptionLabel(formData.organization_type, uiLanguage)
                    : "-"}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/60">
                <p className="text-zinc-500 uppercase text-[11px] tracking-wider">{localizedCopy.summaryIndustry}</p>
                <p className="text-zinc-200 mt-1">
                  {formData.industry ? getLocalizedOptionLabel(formData.industry, uiLanguage) : "-"}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/60">
                <p className="text-zinc-500 uppercase text-[11px] tracking-wider">{localizedCopy.summaryRegion}</p>
                <p className="text-zinc-200 mt-1">{formData.declared_region || detectedRegion || "-"}</p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/60">
                <p className="text-zinc-500 uppercase text-[11px] tracking-wider">{localizedCopy.summaryInvestment}</p>
                <p className="text-zinc-200 mt-1">
                  {formData.investment_range
                    ? investmentLabelByValue[formData.investment_range] || formData.investment_range
                    : "-"}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/60 sm:col-span-2">
                <p className="text-zinc-500 uppercase text-[11px] tracking-wider">{localizedCopy.summaryBusinessGoal}</p>
                <p className="text-zinc-200 mt-1">{formData.business_goal_description || "-"}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderPostSubmitScheduling = () => {
    if (!submitResult || submitResult.source !== "backend") {
      return null;
    }

    return (
      <div className="mt-10 p-6 rounded-2xl border border-cyan-500/30 bg-zinc-950/70 space-y-5">
        <div>
          <h4 className="text-xl text-white font-light">{localizedCopy.scheduleTitle}</h4>
          <p className="text-zinc-400 mt-2 text-sm">
            {localizedCopy.scheduleSubtitle}
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <button
            type="button"
            className={optionButtonClass(authState.provider === "apple")}
            onClick={() => handleAuthProvider("apple")}
            disabled={isAppleAuthenticating}
          >
            {isAppleAuthenticating ? localizedCopy.connecting : localizedCopy.continueApple}
          </button>
          <button
            type="button"
            className={optionButtonClass(authState.provider === "google")}
            onClick={() => handleAuthProvider("google")}
            disabled={isGoogleAuthenticating}
          >
            {isGoogleAuthenticating ? localizedCopy.connecting : localizedCopy.continueGoogle}
          </button>
          <button
            type="button"
            className={optionButtonClass(authState.provider === "magic_link")}
            onClick={() => handleAuthProvider("magic_link")}
          >
            {localizedCopy.continueMagic}
          </button>
        </div>

        {googleClientId ? (
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
              {localizedCopy.officialGoogleButton}
            </p>
            <div ref={googleButtonContainerRef} />
          </div>
        ) : (
          <p className="text-xs text-zinc-500">
            {localizedCopy.googleNotConfigured}
          </p>
        )}

        {authState.provider === "magic_link" && (
          <div className="grid sm:grid-cols-[1fr_auto] gap-3">
            <input
              className="w-full rounded-xl border border-zinc-800 bg-zinc-950/60 text-zinc-200 px-4 py-3"
              placeholder={localizedCopy.magicTokenPlaceholder}
              value={authState.magicToken}
              onChange={(event) =>
                setAuthState((prev) => ({
                  ...prev,
                  magicToken: event.target.value,
                }))
              }
            />
            <button
              type="button"
              onClick={handleVerifyMagicLink}
              className="px-5 py-3 rounded-xl border border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
            >
              {localizedCopy.verify}
            </button>
          </div>
        )}

        {authState.authStatus && <p className="text-sm text-cyan-300">{authState.authStatus}</p>}

        {authState.userId && authState.emailVerified && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-zinc-300 text-sm">
                {formatCopy(localizedCopy.chooseSlot, { timezone: formData.timezone || "UTC" })}
              </p>
              <button
                type="button"
                onClick={loadSlots}
                className="text-xs uppercase tracking-wider text-cyan-400 hover:text-cyan-300"
              >
                {localizedCopy.refreshSlots}
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {slots.length === 0 && <p className="text-zinc-500 text-sm">{localizedCopy.noSlots}</p>}
              {slots.map((slot) => (
                <button
                  type="button"
                  key={slot.id}
                  className={optionButtonClass(selectedSlotId === slot.id)}
                  onClick={() => setSelectedSlotId(slot.id)}
                  disabled={slot.status !== "available"}
                >
                  <div className="text-sm text-zinc-200">{new Date(slot.start_time).toLocaleString()}</div>
                  <div className="text-xs text-zinc-500 mt-1">{slot.status} · {slot.timezone}</div>
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={scheduleAppointment}
              className="w-full px-6 py-4 rounded-xl bg-cyan-400 text-black font-medium hover:bg-cyan-300 transition-colors"
            >
              {localizedCopy.confirmAppointment}
            </button>
          </div>
        )}

        {scheduleStatus && <p className="text-sm text-zinc-300">{scheduleStatus}</p>}
      </div>
    );
  };

  if (isBooting) {
    return (
      <div className="rounded-3xl border border-zinc-800 bg-zinc-950/70 backdrop-blur-xl p-8">
        <p className="text-zinc-300">{localizedCopy.initLoading}</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-zinc-800/80 bg-zinc-950/70 backdrop-blur-2xl shadow-[0_0_50px_rgba(8,145,178,0.08)] p-6 sm:p-8">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-cyan-400 uppercase tracking-[0.25em] text-xs">{localizedCopy.assistantEyebrow}</p>
          <h3 className="text-2xl text-white font-light mt-2">{localizedCopy.assistantTitle}</h3>
        </div>
        <p className="text-zinc-500 text-sm">
          {formatCopy(localizedCopy.stepOf, { current: stepIndex + 1, total: localizedSteps.length })}
        </p>
      </div>

      <div className="h-2 rounded-full bg-zinc-900 overflow-hidden mb-8">
        <div className="h-full bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="mb-6">
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">{currentStep.title}</p>
        <h4 className="text-xl text-white font-light mt-2">{currentStep.question}</h4>
      </div>

      {initStatus && (
        <div className="mb-5 p-3 rounded-xl border border-amber-400/30 bg-amber-500/10 text-amber-200 text-sm">
          {initStatus}
        </div>
      )}

      {renderStep()}

      {errors && (
        <div className="mt-5 p-3 rounded-xl border border-red-400/30 bg-red-500/10 text-red-300 text-sm">
          {errors}
        </div>
      )}

      {!submitResult && (
        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={stepIndex === 0}
            className="px-5 py-3 rounded-xl border border-zinc-700 text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {localizedCopy.back}
          </button>

          {currentStep.key === "summary" ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-3 rounded-xl bg-white text-black font-medium disabled:opacity-60"
            >
              {isSubmitting ? localizedCopy.submitting : localizedCopy.submitAssessment}
            </button>
          ) : (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-3 rounded-xl bg-cyan-400 text-black font-medium"
            >
              {localizedCopy.continue}
            </button>
          )}
        </div>
      )}

      {submitResult && (
        <div className="mt-8 p-5 rounded-2xl border border-cyan-400/40 bg-cyan-500/10 text-cyan-200">
          <p className="font-medium">{localizedCopy.submittedSuccess}</p>
          {submitResult.source === "backend" ? (
            <>
              <p className="text-sm mt-2">
                {localizedCopy.leadScore}: <span className="text-white">{submitResult.lead_score}</span> · {localizedCopy.priority}:{" "}
                <span className="text-white">{submitResult.lead_priority}</span>
              </p>
              <p className="text-sm mt-1">
                {localizedCopy.suggestedOffering}: {submitResult.suggested_offering}
              </p>
            </>
          ) : (
            <p className="text-sm mt-2">
              {uiLanguage === "es"
                ? "Enviado por relay externo de Cloudflare. Cuando el backend esté desplegado, se habilitará clasificación y agenda automática."
                : "Sent via external Cloudflare relay. Automatic qualification and scheduling will be available once backend deployment is ready."}
            </p>
          )}
        </div>
      )}

      {renderPostSubmitScheduling()}
    </div>
  );
};

export default ProjectDiscoveryAssistant;
